import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import ToastContainer from '../components/ui/ToastContainer';

/**
 * Toast context for sharing toast state and methods across the app
 */
const ToastContext = createContext(null);

/**
 * Generate a unique ID for each toast
 * Uses timestamp + random string for uniqueness
 * @returns {string} Unique toast identifier
 */
const generateToastId = () => {
  return `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Default duration for auto-dismiss in milliseconds
 */
const DEFAULT_DURATION = 5000;

/**
 * ToastProvider wraps the app and provides toast functionality
 * Manages toast state and renders the ToastContainer
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  /**
   * Show a new toast notification
   *
   * @param {string} message - Toast message content
   * @param {('success'|'error'|'warning'|'info')} type - Toast variant type
   * @param {number} duration - Auto-dismiss duration in ms (0 for no auto-dismiss)
   * @returns {string} The ID of the created toast
   */
  const showToast = useCallback((message, type = 'info', duration = DEFAULT_DURATION) => {
    const id = generateToastId();

    const newToast = {
      id,
      message,
      type,
      duration,
      createdAt: Date.now(),
    };

    setToasts((prevToasts) => [...prevToasts, newToast]);

    return id;
  }, []);

  /**
   * Hide/dismiss a specific toast by ID
   *
   * @param {string} id - Toast ID to dismiss
   */
  const hideToast = useCallback((id) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  /**
   * Hide all toasts
   */
  const hideAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  /**
   * Convenience methods for specific toast types
   */
  const toast = useMemo(() => ({
    /**
     * Show a success toast
     * @param {string} message - Toast message
     * @param {number} duration - Duration in ms
     * @returns {string} Toast ID
     */
    success: (message, duration = DEFAULT_DURATION) =>
      showToast(message, 'success', duration),

    /**
     * Show an error toast
     * @param {string} message - Toast message
     * @param {number} duration - Duration in ms
     * @returns {string} Toast ID
     */
    error: (message, duration = DEFAULT_DURATION) =>
      showToast(message, 'error', duration),

    /**
     * Show a warning toast
     * @param {string} message - Toast message
     * @param {number} duration - Duration in ms
     * @returns {string} Toast ID
     */
    warning: (message, duration = DEFAULT_DURATION) =>
      showToast(message, 'warning', duration),

    /**
     * Show an info toast
     * @param {string} message - Toast message
     * @param {number} duration - Duration in ms
     * @returns {string} Toast ID
     */
    info: (message, duration = DEFAULT_DURATION) =>
      showToast(message, 'info', duration),
  }), [showToast]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    showToast,
    hideToast,
    hideAllToasts,
    toast,
    toasts,
  }), [showToast, hideToast, hideAllToasts, toast, toasts]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={hideToast} />
    </ToastContext.Provider>
  );
};

/**
 * Hook to access toast functionality
 * Must be used within a ToastProvider
 *
 * @returns {Object} Toast context with showToast, hideToast, hideAllToasts, and toast methods
 * @throws {Error} If used outside of ToastProvider
 *
 * @example
 * // Basic usage
 * const { showToast } = useToast();
 * showToast('Operation successful!', 'success');
 *
 * @example
 * // Using convenience methods
 * const { toast } = useToast();
 * toast.success('Item saved!');
 * toast.error('Something went wrong');
 * toast.warning('Please review your input');
 * toast.info('New updates available');
 *
 * @example
 * // Custom duration (10 seconds)
 * toast.success('This will stay longer', 10000);
 *
 * @example
 * // No auto-dismiss
 * toast.error('Critical error - requires manual dismiss', 0);
 */
export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
};

export default useToast;
